import React, { useContext } from 'react';
import { createHashRouter, RouterProvider, Navigate } from 'react-router-dom';
import { MantineProvider, createTheme } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { DatesProvider } from '@mantine/dates';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '../styles/responsive-fonts.css';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import {
  IconShoppingCart,
  IconChartBar,
  IconBox,
  IconHistory,
  IconTools,
  IconCurrencyDollar
} from '@tabler/icons-react';
import { AuthContext } from './auth';
import ErrorBoundary from '../components/ErrorBoundary';
import SeniorFriendlyApp from '../layouts/SeniorFriendlyApp';
import SeniorLoginPage from '../pages/SeniorLoginPage';
import { LazyComponents } from '../utils/performanceOptimization';
import { Suspense } from 'react';
import { Loader, Center, Text, Stack } from '@mantine/core';

// 懶載入的載入指示器
const LoadingFallback = () => (
  <Center style={{ height: '50vh' }}>
    <Stack align="center" gap="md">
      <Loader size="lg" />
      <Text size="lg" c="dimmed">載入中...</Text>
    </Stack>
  </Center>
);

// 設定 dayjs 中文支援
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.locale('zh-tw');

// 老花友善主題配置（桌機版字型大小定義於此；手機版覆寫見 styles/responsive-fonts.css）
const seniorFriendlyTheme = createTheme({
  fontSizes: {
    xs: '16px',
    sm: '18px', 
    md: '20px',
    lg: '22px',
    xl: '24px',
  },
  spacing: {
    xs: '12px',
    sm: '16px',
    md: '20px',
    lg: '24px',
    xl: '32px',
  },
  radius: {
    xs: '6px',
    sm: '8px',
    md: '10px',
    lg: '12px',
    xl: '16px',
  },
  colors: {
    blue: [
      '#e3f2fd',
      '#bbdefb', 
      '#90caf9',
      '#64b5f6',
      '#42a5f5',
      '#2196f3',
      '#1e88e5',
      '#1976d2',
      '#1565c0',
      '#0d47a1'
    ],
    green: [
      '#e8f5e8',
      '#c8e6c9',
      '#a5d6a7', 
      '#81c784',
      '#66bb6a',
      '#4caf50',
      '#43a047',
      '#388e3c',
      '#2e7d32',
      '#1b5e20'
    ],
    red: [
      '#ffebee',
      '#ffcdd2',
      '#ef9a9a',
      '#e57373',
      '#ef5350',
      '#f44336',
      '#e53935',
      '#d32f2f',
      '#c62828',
      '#b71c1c'
    ]
  },
  primaryColor: 'blue',
  primaryShade: { light: 6, dark: 8 },
  defaultRadius: 'md',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
  headings: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
    sizes: {
      h1: { fontSize: '32px', lineHeight: '1.4' },
      h2: { fontSize: '28px', lineHeight: '1.4' },
      h3: { fontSize: '24px', lineHeight: '1.4' },
      h4: { fontSize: '22px', lineHeight: '1.4' },
      h5: { fontSize: '20px', lineHeight: '1.4' },
      h6: { fontSize: '18px', lineHeight: '1.4' },
    }
  },
  components: {
    Button: {
      defaultProps: {
        size: 'lg',
      },
      styles: {
        root: {
          minHeight: '48px',
          fontSize: 'var(--mantine-font-size-sm)',
          fontWeight: 600,
        }
      }
    },
    TextInput: {
      defaultProps: {
        size: 'lg',
      },
      styles: {
        input: {
          fontSize: 'var(--mantine-font-size-sm)',
          minHeight: '48px',
        }
      }
    },
    Select: {
      defaultProps: {
        size: 'lg',
      },
      styles: {
        input: {
          fontSize: 'var(--mantine-font-size-sm)',
          minHeight: '48px',
        }
      }
    }
  }
});

const PrivateRoute = ({ children }) => {
  const { user } = useContext(AuthContext);
  return user ? children : <Navigate to="/login" />;
};

export const seniorRoute = [
  {
    path: '/login',
    element: <SeniorLoginPage />
  },
  {
    path: '/',
    element: (
      <PrivateRoute>
        <SeniorFriendlyApp />
      </PrivateRoute>
    ),
    children: [
      {
        path: '/',
        icon: IconShoppingCart,
        title: '進貨輸入',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LazyComponents.SeniorKeyinPage />
          </Suspense>
        )
      },
      {
        path: '/board',
        icon: IconChartBar,
        title: '統計查詢',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LazyComponents.SeniorBoardPage />
          </Suspense>
        )
      },
      {
        path: '/product',
        icon: IconBox,
        title: '產品管理',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LazyComponents.SeniorProdPage />
          </Suspense>
        )
      },
      {
        path: '/history/',
        icon: IconHistory,
        title: '進貨記錄',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LazyComponents.SeniorHistoryPage />
          </Suspense>
        )
      },
      {
        path: '/price-change',
        icon: IconCurrencyDollar,
        title: '價格異動',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LazyComponents.PriceChangePage />
          </Suspense>
        )
      },
      {
        path: '/product/:pid',
        icon: IconHistory,
        title: '產品細節',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LazyComponents.SeniorHistoryPage />
          </Suspense>
        ),
        hideMenu: true
      },
      {
        path: '/fix',
        icon: IconTools,
        title: '資料維護',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LazyComponents.SeniorFixPage />
          </Suspense>
        )
      }
    ]
  }
];

const router = createHashRouter(seniorRoute, {
  future: {
    v7_startTransition: true
  }
});

export const SeniorAppRouterProvider = () => {
  return (
    <ErrorBoundary>
      <MantineProvider theme={seniorFriendlyTheme}>
        <ModalsProvider>
          <DatesProvider settings={{ locale: 'zh-tw' }}>
            <Notifications position="top-right" />
            <RouterProvider router={router} />
          </DatesProvider>
        </ModalsProvider>
      </MantineProvider>
    </ErrorBoundary>
  );
};
